import { ICellProps } from "@components/table/cells/Cells.types";
import {
    HoverContentWrapper,
    Resizer,
    SideContent,
    SideContentWrapper,
    StyledBodyCell,
    StyledBodyCellContent
} from "@components/table/Rows.styles";
import Tooltip from "@components/tooltip";
import { getRectNormalized, normalizeProp } from "@utils/zoom.utils";
import React, { PureComponent } from "react";

import { MouseButton, TextAlign } from "../../../enums";
import TestIds from "../../../testIds";

export class BodyCell extends PureComponent<ICellProps> {
    static defaultProps: Partial<ICellProps> = {
        level: 0,
        onlyShowTooltipWhenChildrenOverflowing: true
    };

    cellRef = React.createRef<HTMLDivElement>();
    initOffset: number;

    handleMouseDown = (event: React.MouseEvent): void => {
        if (event.button !== MouseButton.MainButton) {
            return;
        }

        event.preventDefault(); // disable text selection
        event.stopPropagation();
        this.initOffset = getRectNormalized(this.cellRef.current).right - normalizeProp(event.clientX);

        window.document.addEventListener("mousemove", this.handleMouseMove);
        // use capture to prevent row click after resizing
        window.document.addEventListener("click", this.handleMouseUp, true);
    };

    // prevent click event from propagation to row, to prevent row selection when resizing
    handleMouseClick = (event: React.MouseEvent | MouseEvent): void => {
        event.stopPropagation();
        event.preventDefault();
    };

    handleColumnResize = (event: MouseEvent, end?: boolean): void => {
        let newWidth;

        if (this.props.onColumnResize) {
            const offset = getRectNormalized(this.cellRef.current).right - normalizeProp(event.clientX);
            newWidth = (normalizeProp(event.clientX) - getRectNormalized(this.cellRef.current).left) + this.initOffset;

            this.props.onColumnResize({
                id: this.props.id,
                columnIndex: this.props.columnIndex,
                oldWidth: this.props.width,
                width: newWidth,
                delta: newWidth - this.props.width,
                stepDelta: this.initOffset - offset,
                level: this.props.level,
                end
            });
        }
    };

    handleMouseMove = (event: MouseEvent): void => {
        this.handleColumnResize(event);
    };

    handleMouseUp = (event: MouseEvent): void => {
        event.stopPropagation();
        event.preventDefault();
        this.handleColumnResize(event, true);

        window.document.removeEventListener("mousemove", this.handleMouseMove);
        window.document.removeEventListener("click", this.handleMouseUp, true);
    };

    renderContent = (): React.ReactElement => {
        // overflowing text is supposed to have tooltip
        return (
                <Tooltip content={this.props.tooltip ?? this.props.children}
                         status={this.props.tooltipStatus}
                         onlyShowWhenChildrenOverflowing={this.props.onlyShowTooltipWhenChildrenOverflowing}>
                    {(ref) => {
                        return (
                                <StyledBodyCellContent data-column-index={this.props.columnIndex}
                                                       $allowOverflow={this.props.allowOverflow}
                                                       $stretchContent={this.props.stretchContent}
                                                       $width={this.props.width}
                                                       $minWidth={this.props.minWidth}
                                                       $isLoading={this.props.isLoading}
                                                       data-testid={TestIds.TableCellContent}
                                                       ref={ref}>
                                    {this.props.isLoading ? null : this.props.children}
                                </StyledBodyCellContent>
                        );
                    }}
                </Tooltip>
        );
    };

    renderAfterContent = (): React.ReactElement => {
        const afterContent = this.props.afterContent ?? (this.props.textAlign === TextAlign.Left && this.props.oppositeAlignmentContent);
        if (!afterContent) {
            return null;
        }
        return (
                <SideContentWrapper data-testid={TestIds.TableCellAfterContent} $isAfter>
                    <SideContent $minWidth={this.props.afterContentMinWidth}>
                        {afterContent}
                    </SideContent>
                </SideContentWrapper>
        );
    };

    renderBeforeContent = (): React.ReactElement => {
        const beforeContent = this.props.beforeContent ?? (this.props.textAlign === TextAlign.Right && this.props.oppositeAlignmentContent);
        if (!beforeContent) {
            return null;
        }
        return (
                <SideContentWrapper data-testid={TestIds.TableCellBeforeContent}>
                    <SideContent $minWidth={this.props.beforeContentMinWidth}>
                        {beforeContent}
                    </SideContent>
                </SideContentWrapper>
        );
    };

    renderHoverContent = (): React.ReactElement => {
        return (
                <HoverContentWrapper data-testid={TestIds.TableCellHoverContent}
                                     $textAlign={this.props.textAlign}>
                    {this.props.hoverContent}
                </HoverContentWrapper>
        );
    };

    render() {
        const hasHoverContent = !!this.props.hoverContent;
        return (
                <StyledBodyCell ref={this.cellRef}
                                $sticky={this.props.sticky}
                                $isForPrint={this.props.isForPrint}
                                $isColumnHighlighted={this.props.isColumnHighlighted}
                                $isRowHighlighted={this.props.isRowHighlighted}
                                $isDisabled={this.props.isDisabled}
                                $border={this.props.border}
                                $width={this.props.width}
                                $first={this.props.first}
                                $last={this.props.last}
                                $textAlign={this.props.textAlign}
                                $hasHoverContent={hasHoverContent}
                                $hasAction={this.props.hasAction}
                                $keepSpaceForStatus={this.props.keepSpaceForStatus}
                                role="cell"
                                $isHierarchy={this.props.isHierarchy}
                                $customBackgroundColor={this.props.customBackgroundColor}
                                data-testid={TestIds.TableCell}>
                    {this.renderBeforeContent()}
                    {this.renderContent()}
                    {this.renderAfterContent()}
                    {hasHoverContent && this.renderHoverContent()}
                    {this.props.onColumnResize &&
                            <Resizer onMouseDown={this.handleMouseDown} onClick={this.handleMouseClick}/>
                    }
                </StyledBodyCell>
        );
    }
}